import { useState, useRef } from "react";
import { useDispatch } from "react-redux";
import InputText from "../../../components/Input/InputText";
import ErrorText from "../../../components/Typography/ErrorText";
import { showNotification } from "../../common/headerSlice";
import { addNewMessage } from "../messageSlice";
import api from "../../../utils/axios";
import { triggerRefresh } from "../../../utils/refreshRegistry";

const INITIAL_MESSAGE_OBJ = {
  date: "",
  title: "",
  speaker: "",
  audioUrl: "",
  videoUrl: "",
};

async function addMessage(e) {
  try {
    const newMessage = await api.post("api/messages", e);
    return newMessage;
  } catch (error) {
    console.error("Error adding message:", error);
    throw error;
  }
}

function AddMessageModalBody({ closeModal }) {
  const dispatch = useDispatch();
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [messageObj, setMessageObj] = useState(INITIAL_MESSAGE_OBJ);
  const [saving, setSaving] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [audioUrlKey, setAudioUrlKey] = useState(0); // Key to force Audio InputText re-render
  const fileInputRef = useRef(null);

  const validate = () => {
    const newErrors = {};
    const objDate = new Date(messageObj.date.trim());
    if (messageObj.date.trim() === "") newErrors.date = "Date is required!";
    else if (objDate.toString() === "Invalid Date") newErrors.date = "Date is invalid!";
    if (messageObj.title.trim() === "") newErrors.title = "Title is required!";
    if (messageObj.speaker.trim() === "") newErrors.speaker = "Speaker is required!";
    // Check that at least one of audio URL/file or video URL is provided
    if (!selectedFile && !messageObj.audioUrl.trim() && (!messageObj.videoUrl || !messageObj.videoUrl.trim())) {
      const urlError = "Either an Audio URL/File or a Video URL is required!";
      newErrors.audioUrl = urlError;
      newErrors.videoUrl = urlError;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveNewMessage = async () => {
    if (!validate()) return;

    // If file is selected but no URL, we need to upload it first
    // If URL is provided, we can proceed directly
    let finalAudioUrl = messageObj.audioUrl;
    setSaving(true);
    setServerError("");

    try {
      // Upload file first if one is selected and no URL provided
      // If URL is provided, it takes precedence over the selected file
      if (selectedFile && !messageObj.audioUrl.trim()) {
        if (!messageObj.date.trim()) {
          setSaving(false);
          setErrors((prev) => ({ ...prev, date: "Date is required for file upload!" }));
          return;
        }

        const formData = new FormData();
        formData.append("audioFile", selectedFile);
        formData.append("date", messageObj.date);

        const uploadResponse = await api.post(
          "/api/messages/upload-audio",
          formData,
          {
            headers: { "Content-Type": "multipart/form-data" },
            timeout: 300000, // 5 minutes timeout for large file uploads
            onUploadProgress: (progressEvent) => {
              // Optional: Could show upload progress here
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              console.log(`Upload progress: ${percentCompleted}%`);
            },
          },
        );

        finalAudioUrl = uploadResponse.data.url;
        // Update the URL field to show the uploaded file's public URL
        setMessageObj({ ...messageObj, audioUrl: finalAudioUrl });
        // Clear selected file so URL field becomes visible
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        // Force InputText to re-render with new URL value
        setAudioUrlKey((prev) => prev + 1);
      } else if (messageObj.audioUrl.trim()) {
        // Use provided URL (file will be ignored if one was selected)
        finalAudioUrl = messageObj.audioUrl;
      } else if (messageObj.videoUrl && messageObj.videoUrl.trim()) {
        // Video URL is provided, so audio is optional
        finalAudioUrl = "";
      } else {
        setSaving(false);
        setErrors((prev) => ({ ...prev, audioUrl: "Either an Audio URL/File or a Video URL is required!" }));
        return;
      }

      // Create the message
      let newMessageObj = {
        date: messageObj.date,
        title: messageObj.title,
        speaker: messageObj.speaker,
        audioUrl: finalAudioUrl,
        videoUrl: messageObj.videoUrl,
      };

      await addMessage(newMessageObj);
      dispatch(addNewMessage({ newMessageObj }));
      dispatch(showNotification({ message: "New Message Added!", status: 1 }));
      closeModal();
      triggerRefresh("messages");
    } catch (error) {
      console.error("Error saving message:", error);
      const errorMsg =
        error.response?.data?.message ||
        "Failed to save message. Please try again.";
      setServerError(errorMsg);
      dispatch(showNotification({ message: errorMsg, status: 0 }));
    } finally {
      setSaving(false);
    }
  };

  const updateFormValue = ({ updateType, value }) => {
    setErrors((prev) => ({ ...prev, [updateType]: "" }));
    setServerError("");
    // If URL is being updated and it has a value, clear the selected file
    if (updateType === "audioUrl" && value.trim()) {
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
    setMessageObj({ ...messageObj, [updateType]: value });
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) {
      setSelectedFile(null);
      return;
    }

    // Validate file type
    const allowedTypes = [
      "audio/mpeg", "audio/mp3", "audio/wav", "audio/wave",
      "audio/x-wav", "audio/ogg", "audio/webm", "audio/mp4",
      "audio/aac", "audio/flac",
    ];

    if (!allowedTypes.includes(file.type)) {
      setErrors((prev) => ({ ...prev, audioUrl: "Invalid file type. Only audio files are allowed." }));
      e.target.value = ""; // Reset file input
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    setErrors((prev) => ({ ...prev, audioUrl: "" }));
    // Clear URL when file is selected
    setMessageObj({ ...messageObj, audioUrl: "" });
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    // Clear URL field when file is removed
    setMessageObj({ ...messageObj, audioUrl: "" });
  };

  return (
    <>
      <InputText
        type="date"
        defaultValue={messageObj.date ? messageObj.date.slice(0, 10) : ""}
        updateType="date"
        containerStyle="mt-4"
        labelTitle="Date"
        updateFormValue={updateFormValue}
        errorMessage={errors.date}
      />

      <InputText
        type="text"
        defaultValue={messageObj.title}
        updateType="title"
        containerStyle="mt-4"
        labelTitle="Title"
        updateFormValue={updateFormValue}
        errorMessage={errors.title}
      />

      <InputText
        type="text"
        defaultValue={messageObj.speaker}
        updateType="speaker"
        containerStyle="mt-4"
        labelTitle="Speaker"
        updateFormValue={updateFormValue}
        errorMessage={errors.speaker}
      />

      <div className={`form-control w-full mt-4`}>
        <label className="label">
          <span className="label-text text-base-content">
            Upload Audio File{" "}
            {messageObj.audioUrl.trim() && "(disabled - URL entered)"}
          </span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="file"
            ref={fileInputRef}
            accept="audio/*"
            onChange={handleFileSelect}
            disabled={saving || !!messageObj.audioUrl.trim()}
            className="file-input file-input-bordered w-full max-w-xs"
          />
        </div>
        {selectedFile && (
          <label className="label">
            <span className="label-text-alt text-info">
              Selected: {selectedFile.name} (will upload on save)
            </span>
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={handleRemoveFile}
              disabled={saving}
            >
              Remove
            </button>
          </label>
        )}
      </div>

      <InputText
        key={audioUrlKey}
        type="url"
        defaultValue={messageObj.audioUrl}
        updateType="audioUrl"
        containerStyle="mt-4"
        labelTitle="Audio URL (or upload file above)"
        placeholder={
          selectedFile
            ? "File selected - URL disabled"
            : "Enter Audio URL or upload file"
        }
        updateFormValue={updateFormValue}
        disabled={!!selectedFile || saving}
        errorMessage={errors.audioUrl}
      />

      <InputText
        type="url"
        defaultValue={messageObj.videoUrl}
        updateType="videoUrl"
        containerStyle="mt-4"
        labelTitle="Video URL"
        placeholder="Enter URL"
        updateFormValue={updateFormValue}
        errorMessage={errors.videoUrl}
      />

      {serverError && <ErrorText styleClass="mt-4">{serverError}</ErrorText>}
      <div className="modal-action">
        <button className="btn btn-ghost" onClick={() => closeModal()}>
          Cancel
        </button>
        <button
          className="btn btn-primary px-6"
          onClick={() => saveNewMessage()}
          disabled={saving}
        >
          {saving ? (
            <>
              <span className="loading loading-spinner loading-sm"></span>
              Saving...
            </>
          ) : (
            "Save"
          )}
        </button>
      </div>
    </>
  );
}

export default AddMessageModalBody;
